import { api } from "./api";
import {
  HotlineAvailableQuestionnaire,
  HotlineAvailableMessage,
  HotlineScope,
  HotlineQuestionnairePolicy,
  HotlineQuestionnairePolicySaveRequest,
  HotlineRequirementsResult,
} from "../types/hotlineQuestionnaire.types";

interface ListResponse {
  success: boolean;
  policies: HotlineQuestionnairePolicy[];
}

interface SingleResponse {
  success: boolean;
  policy: HotlineQuestionnairePolicy | null;
}

interface MessagesResponse {
  success: boolean;
  messages: HotlineAvailableMessage[];
}

interface QuestionnairesResponse {
  success: boolean;
  questionnaires: HotlineAvailableQuestionnaire[];
}

interface ScopeResponse {
  success: boolean;
  scope: HotlineScope | null;
}

/**
 * The hotline's returning-caller questionnaire rule. The server scopes every call to the caller's
 * company, so no companyId is sent.
 */
export const hotlineQuestionnaireApi = api.injectEndpoints({
  endpoints: (build) => ({
    getHotlinePolicies: build.query<HotlineQuestionnairePolicy[], void>({
      query: () => ({
        url: "/HotlineQuestionnairePolicies",
        method: "GET",
      }),
      transformResponse: (response: ListResponse) => response?.policies ?? [],
      providesTags: ["HotlineQuestionnairePolicies"],
    }),

    /**
     * The company's authored SMS messages. Only the choice lives here — the wording is edited on
     * the SMS screen under /protocols/sms.
     */
    getHotlineAvailableMessages: build.query<HotlineAvailableMessage[], void>({
      query: () => ({
        url: "/HotlineQuestionnairePolicies/available-messages",
        method: "GET",
      }),
      transformResponse: (response: MessagesResponse) => response?.messages ?? [],
      providesTags: ["HotlineQuestionnairePolicies"],
    }),

    /** The questionnaires a policy can gate on. */
    getHotlineAvailableQuestionnaires: build.query<HotlineAvailableQuestionnaire[], void>({
      query: () => ({
        url: "/HotlineQuestionnairePolicies/available-questionnaires",
        method: "GET",
      }),
      transformResponse: (response: QuestionnairesResponse) => response?.questionnaires ?? [],
      providesTags: ["HotlineQuestionnairePolicies"],
    }),

    /** Who the rule applies to. Configured on the company settings page. */
    getHotlineScope: build.query<HotlineScope, void>({
      query: () => ({
        url: "/HotlineQuestionnairePolicies/scope",
        method: "GET",
      }),
      transformResponse: (response: ScopeResponse) =>
        response?.scope ?? { enabled: false, includesSubsidiaries: false },
      providesTags: ["HotlineQuestionnairePolicies"],
    }),

    setHotlineScope: build.mutation<HotlineScope | null, HotlineScope>({
      query: (body) => ({
        url: "/HotlineQuestionnairePolicies/scope",
        method: "POST",
        body,
      }),
      transformResponse: (response: ScopeResponse) => response?.scope ?? null,
      invalidatesTags: ["HotlineQuestionnairePolicies"],
    }),

    saveHotlinePolicy: build.mutation<
      HotlineQuestionnairePolicy | null,
      HotlineQuestionnairePolicySaveRequest
    >({
      query: (body) => ({
        url: "/HotlineQuestionnairePolicies",
        method: "POST",
        body,
      }),
      transformResponse: (response: SingleResponse) => response?.policy ?? null,
      invalidatesTags: ["HotlineQuestionnairePolicies"],
    }),

    /**
     * Who owes the questionnaire right now, and what has been done about it.
     *
     * Not tagged against the policy cache: policies change rarely and these change constantly,
     * so sharing an invalidation tag would refetch the wrong one of the two every time.
     */
    getHotlineRequirements: build.query<HotlineRequirementsResult, { openOnly?: boolean } | void>({
      query: (args) => ({
        url: `/HotlineQuestionnairePolicies/requirements?openOnly=${
          args && args.openOnly ? "true" : "false"
        }`,
        method: "GET",
      }),
      transformResponse: (response: {
        total?: number;
        truncated?: boolean;
        requirements?: HotlineRequirementsResult["requirements"];
      }) => ({
        total: response?.total ?? 0,
        truncated: response?.truncated ?? false,
        requirements: response?.requirements ?? [],
      }),
      providesTags: ["HotlineQuestionnaireRequirements"],
    }),

    /** Deactivates rather than deletes, so past requirements keep pointing at something. */
    deactivateHotlinePolicy: build.mutation<{ success: boolean }, number>({
      query: (policyId) => ({
        url: `/HotlineQuestionnairePolicies/${policyId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["HotlineQuestionnairePolicies"],
    }),
  }),
});

export const {
  useGetHotlinePoliciesQuery,
  useGetHotlineAvailableMessagesQuery,
  useGetHotlineAvailableQuestionnairesQuery,
  useGetHotlineScopeQuery,
  useSetHotlineScopeMutation,
  useSaveHotlinePolicyMutation,
  useDeactivateHotlinePolicyMutation,
  useGetHotlineRequirementsQuery,
} = hotlineQuestionnaireApi;
